import { useQuery } from '@tanstack/react-query'
import { jwtDecode } from 'jwt-decode'
import { observer } from 'mobx-react'
import { useEffect, useState, useContext } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useLocation, Link } from 'react-router'

import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/elements/dropdown-menu'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/elements/form'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/elements/input-otp'
import { claimInvitation, getUser, loginConfirm, resendLoginCode } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const CODE_LENGTH = 6

const MultiFactorAuthentication = observer(() => {
  // Context
  const navigate = useNavigate()
  const location = useLocation()
  const { setUser, setUserId, setTokens, user, userId } = useContext(UserStoreContext)

  // State
  const [loading, setLoading] = useState(false)
  const [loadingResend, setLoadingResend] = useState(false)

  // Data Loading
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const { error, data: updatedUser } = useQuery({
    queryKey: ['users', userId],
    queryFn: () => getUser(userId),
    enabled: !!userId,
  })

  const form = useForm({
    defaultValues: {
      code: null,
    },
  })

  useEffect(() => {
    setLoading(false)

    if (updatedUser) {
      setUser(updatedUser)
    } else if (error) {
      handleError(error)
    }
  }, [error, setUser, updatedUser])

  /**
   * Handles form submission
   * - Redirects to dashboard if successful
   * @param {object} data
   */
  const onSubmit = async (data) => {
    setLoading(true)
    const response = await loginConfirm({ code: data.code, email: user.email }, handleError)

    if (response) {
      const decoded = jwtDecode(response.access)
      setTokens(response)

      // Arriving from an invitation's "sign in to link" path (RSCX-351):
      // the tokens above authenticate the claim, and it must finish before
      // setUserId triggers the user fetch — otherwise the fetched profile
      // races the claim and misses the newly linked email.
      const invitationToken = sessionStorage.getItem('claimInvitationToken')
      if (invitationToken) {
        sessionStorage.removeItem('claimInvitationToken')
        try {
          const claimed = await claimInvitation(invitationToken)
          handleSuccess(`Invitation linked — ${claimed.email} was added to your account`)
        } catch (err) {
          handleError(err.message)
        }
      }

      setUserId(decoded.user_id)
    } else {
      setLoading(false)
    }
  }

  /**
   * Re-sends the login code.
   *
   * @param {string} device - 'Email' or 'Phone'
   * @param {string} [emailId] - which of the account's verified addresses to send to, from the
   *   login response's `mfaAddresses` (RSCX-377). Omit to re-send to the address signed in with.
   * @param {string} [key] - identifies which control is spinning
   */
  const submitResendForm = async (device, emailId, key = device) => {
    if (!location.state?.password) {
      navigate(`/login`, { replace: true })
      return
    }

    const results = await resendLoginCode(
      { mfaDevice: device, emailId, email: user.email, password: location.state.password },
      handleError,
      (l) => setLoadingResend(l ? key : false),
    )

    if (results) {
      setUser({ email: user.email, ...results })
      handleSuccess(results.message)
    }
  }

  // Every other verified address on the account, redacted by the API. Offered
  // as alternate destinations when the code didn't reach the one signed in
  // with — typically an employer address the user has lost (RSCX-377).
  const otherAddresses = (user.mfaAddresses ?? []).filter(
    (address) => address.id !== user.mfaAddressId,
  )

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-lg">
        <div className="flex flex-col gap-6">
          <Card className="overflow-hidden p-0">
            <CardContent className="p-0">
              <Form {...form}>
                <form className="p-6 md:p-8" onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="flex flex-col gap-6">
                    <div className="flex flex-col items-center text-center">
                      <h1>Two Factor Authentication</h1>
                      <p className="text-muted-foreground mt-2 text-center text-sm text-balance">
                        Or{' '}
                        <Link
                          to="/login"
                          className="text-primary ml-auto underline-offset-2 hover:underline"
                        >
                          Log In With A Different Account
                        </Link>
                      </p>

                      <p className="text-primary mt-2 text-center text-sm">
                        Enter the verification code sent to{' '}
                        <span className="font-bold">{user.address}</span>.
                      </p>
                    </div>

                    <FormField
                      control={form.control}
                      disabled={loading}
                      name="code"
                      render={({ field }) => (
                        <FormItem className="justify-center">
                          <FormControl>
                            <InputOTP maxLength={CODE_LENGTH} {...field}>
                              <InputOTPGroup>
                                <InputOTPSlot index={0} />
                                <InputOTPSlot index={1} />
                                <InputOTPSlot index={2} />
                                <InputOTPSlot index={3} />
                                <InputOTPSlot index={4} />
                                <InputOTPSlot index={5} />
                              </InputOTPGroup>
                            </InputOTP>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                      rules={{ required: true }}
                    />

                    <div className="flex flex-col justify-center gap-6 sm:flex-row sm:gap-3">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => submitResendForm('Email')}
                        type="button"
                        loading={loadingResend === 'Email'}
                      >
                        Resend Code Via Email
                      </Button>

                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => submitResendForm('Phone')}
                        type="button"
                        loading={loadingResend === 'Phone'}
                      >
                        Resend Code Via Phone
                      </Button>

                      {otherAddresses.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="secondary"
                              type="button"
                              loading={otherAddresses.some((a) => a.id === loadingResend)}
                            >
                              Send To Another Email
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center">
                            {otherAddresses.map((address) => (
                              <DropdownMenuItem
                                key={address.id}
                                onSelect={() => submitResendForm('Email', address.id, address.id)}
                              >
                                {address.address}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    <Button loading={loading} type="submit" className="w-full">
                      Verify
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
})

export default MultiFactorAuthentication
